import { GameConfig } from "@/config/GameConfig";
import type { Direction } from "@/core/types";
import type { DirectionHandler, InputSource } from "./InputSource";
import { getAudioManager } from "@/audio/AudioManager";

interface TouchPoint {
  x: number;
  y: number;
}

/**
 * PR32 (touch fairness pass): how confidently a swipe's dominant axis was
 * read, alongside the naive dominant-axis direction itself - see
 * `classifySwipeCandidate()`'s own doc. `isAmbiguous` is what drives both
 * the near-45° confirmation gate in `onTouchMove` and the
 * current-direction-aware override in `preferActionableDirection()`.
 */
interface SwipeCandidate {
  readonly direction: Direction;
  readonly isAmbiguous: boolean;
}

/**
 * PR32 (touch fairness pass): how close a swipe's two axis displacements
 * have to be (as a ratio of the smaller to the larger) before it counts
 * as a near-45° "ambiguous" read rather than a confidently dominant one.
 * 0.85 keeps the ambiguous band narrow - roughly the 40.4deg-49.6deg
 * range around each diagonal (about a tenth of the 90deg quadrant) - so
 * an ordinary diagonal thumb swipe (which usually leans well clear of a
 * true 45deg) is never affected, while a genuinely coin-flip reading
 * (real near-45deg jitter, or the "current direction vs. a real turn"
 * case `preferActionableDirection()` handles) is caught. One centralized
 * constant, not several scattered magic numbers.
 */
const DIAGONAL_AMBIGUITY_RATIO = 0.85;

/**
 * Classifies a swipe candidate for a displacement of `(dx, dy)` from an
 * anchor point, or `null` if it hasn't crossed `minDistance` yet. The
 * direction is decided by the dominant axis (whichever of dx/dy is
 * larger in magnitude), exactly as before; `isAmbiguous` additionally
 * flags a near-45deg reading where the two axes are close enough in
 * magnitude that the dominant one could easily be noise rather than
 * genuine intent (see `DIAGONAL_AMBIGUITY_RATIO`). Pure and
 * framework-independent so `onTouchMove` (mid-gesture) and `onTouchEnd`
 * (PR31: end-of-gesture, see that handler's own doc) can share the exact
 * same rule, rather than risking the two diverging.
 */
function classifySwipeCandidate(dx: number, dy: number, minDistance: number): SwipeCandidate | null {
  const absX = Math.abs(dx);
  const absY = Math.abs(dy);
  const larger = Math.max(absX, absY);

  if (larger < minDistance) {
    return null;
  }

  const direction: Direction = absX > absY ? (dx > 0 ? "right" : "left") : dy > 0 ? "down" : "up";
  const smaller = Math.min(absX, absY);
  const isAmbiguous = smaller / larger >= DIAGONAL_AMBIGUITY_RATIO;
  return { direction, isAmbiguous };
}

/** The axis perpendicular to `direction`, using `dx`/`dy`'s own sign to pick which of the two perpendicular directions. */
function perpendicularDirection(direction: Direction, dx: number, dy: number): Direction {
  return direction === "left" || direction === "right" ? (dy > 0 ? "down" : "up") : dx > 0 ? "right" : "left";
}

/**
 * PR32 (touch fairness pass) - the "current-direction-aware" resolution:
 * a near-45deg (`isAmbiguous`) swipe whose naive dominant-axis read
 * exactly matches the snake's CURRENT direction is reinterpreted as the
 * perpendicular turn instead. Continuing the current direction takes no
 * player action at all, so a coin-flip-close reading that happens to
 * land on "the direction already being taken" is far more likely a turn
 * attempt along the other, nearly-as-strong axis than a deliberate,
 * pointless re-swipe of the direction already in progress.
 *
 * Deliberately narrow: this NEVER fires for a confidently dominant
 * (non-ambiguous) read, and NEVER reinterprets an illegal reverse (a
 * naive read of the OPPOSITE of the current direction) - `direction`
 * only ever equals `currentDirection` here, never its opposite, so this
 * can't be confused with "guessing" between two unrelated legal turns
 * from a rejected reverse. That case is handled separately - see
 * `onTouchMove`'s own doc for why a rejected reverse instead holds the
 * gesture's anchor and lets a later sample resolve it.
 */
function preferActionableDirection(candidate: SwipeCandidate, dx: number, dy: number, currentDirection: Direction): Direction {
  if (!candidate.isAmbiguous || candidate.direction !== currentDirection) {
    return candidate.direction;
  }
  return perpendicularDirection(candidate.direction, dx, dy);
}

/**
 * PR31 follow-up fix (BLOCKER 2 investigation): finds the `Touch` matching
 * `identifier` in a `TouchList`/array, or `null`. See `TouchInput`'s own
 * doc for why every handler below looks a touch up by its stable
 * `identifier` instead of blindly reading index `0` - `TouchList` order is
 * NOT a reliable proxy for "the touch this gesture is tracking" once more
 * than one touch is simultaneously active.
 */
function findTouchById(touches: ArrayLike<Touch>, identifier: number): Touch | null {
  for (let i = 0; i < touches.length; i++) {
    if (touches[i]!.identifier === identifier) return touches[i]!;
  }
  return null;
}

/**
 * Swipe-based control for touch devices.
 *
 * Direction is decided by the dominant axis of the swipe (whichever of
 * dx/dy is larger in magnitude), and only fires once a minimum distance
 * is crossed so small, accidental touches don't change direction.
 *
 * The "anchor" point is advanced every time a swipe fires (rather than
 * only on touch end), so a single continuous drag can emit several
 * direction changes in a row - this keeps controls feeling immediate.
 *
 * PR31: `touchend` also evaluates the gesture's final displacement (see
 * `onTouchEnd`'s own doc) - a real, previously-unhandled missed-swipe path
 * on a fast flick that produces zero (or zero threshold-crossing)
 * `touchmove` events before lifting.
 *
 * PR31 follow-up fix (BLOCKER 2 investigation): tracks exactly ONE touch
 * per gesture by its stable `Touch.identifier`, set once in `onTouchStart`
 * and looked up (never assumed to be index `0`) in every later event
 * (`findTouchById()`). A real phone very plausibly has more than one
 * simultaneous contact during a swipe - a resting palm heel, a second
 * finger brushing the screen while the hand re-grips to reach upward/
 * sideways - and `TouchList` order across events is NOT guaranteed to
 * keep the intended finger at index `0` once that happens; blindly
 * reading `touches[0]`/`changedTouches[0]` could silently start tracking,
 * mid-swipe, an entirely different (near-stationary) contact, making a
 * real, deliberate swipe measure almost no displacement at all - exactly
 * the "I made what feels like a clear swipe and the snake does not turn"
 * symptom, with no threshold low enough to fix it (the tracked point
 * genuinely never moved). A second touch beginning while the first is
 * still tracked is ignored entirely (`onTouchStart` no-ops whenever
 * `activeTouchId` is already set) - this game's swipe gesture is, and
 * only ever needs to be, single-touch; a `touchend`/`touchcancel` for any
 * touch OTHER than the tracked one is likewise ignored (the real gesture,
 * still in progress, must never be reset by an unrelated contact lifting).
 *
 * PR32 (touch fairness pass): two further refinements, both purely about
 * interpreting the gesture BEFORE it reaches `InputQueue` - no gameplay
 * forgiveness, no delay, no prediction. (1) A near-45deg ("ambiguous")
 * reading whose naive dominant axis happens to match the CURRENT
 * direction is reinterpreted as the perpendicular turn instead, since
 * continuing the current direction needs no swipe at all - see
 * `preferActionableDirection()`. (2) A direction rejected as an illegal
 * reverse no longer re-anchors - the gesture's anchor is held so the
 * NEXT sample's displacement is measured from where the gesture actually
 * started, giving a real intended turn a fair chance instead of losing
 * it to a reset anchor - see `onTouchMove`'s own doc.
 */
/**
 * Real touchmove crossings that happen THIS early into a fresh gesture
 * are exactly the ones a brief thumb-settling "hook" can produce on its
 * own (see `TouchInput`'s own doc) - anything crossing later has had time
 * to reflect genuine, sustained motion. Only crossings inside this window
 * get the one-sample confirmation in `onTouchMove` below; every later
 * crossing, and every `touchend` fallback (which has no further sample to
 * confirm against anyway), fires exactly as before - zero added latency
 * for the overwhelming majority of real swipes.
 */
const EARLY_COMMIT_WINDOW_MS = 40;

export class TouchInput implements InputSource {
  private anchor: TouchPoint | null = null;
  private activeTouchId: number | null = null;
  private gestureStartTime = 0;
  private pendingDirection: Direction | null = null;

  private readonly onTouchStart = (event: TouchEvent): void => {
    getAudioManager().unlock();
    event.preventDefault();
    if (this.activeTouchId !== null) return; // Already tracking a touch - an incidental second contact is never allowed to hijack or reset the gesture in progress.
    const touch = event.changedTouches[0];
    if (!touch) return;
    this.activeTouchId = touch.identifier;
    this.anchor = { x: touch.clientX, y: touch.clientY };
    this.gestureStartTime = event.timeStamp;
    this.pendingDirection = null;
  };

  private readonly onTouchMove = (event: TouchEvent): void => {
    event.preventDefault();
    if (this.activeTouchId === null || !this.anchor) return;
    const touch = findTouchById(event.touches, this.activeTouchId);
    if (!touch) return; // The tracked touch isn't present in this event - some other touch moved; our gesture is unaffected.

    const dx = touch.clientX - this.anchor.x;
    const dy = touch.clientY - this.anchor.y;
    const candidate = classifySwipeCandidate(dx, dy, GameConfig.input.swipeMinDistance);
    if (!candidate) {
      this.pendingDirection = null;
      return;
    }

    // PR31 follow-up fix (BLOCKER 1 investigation) + PR32 (touch fairness
    // pass): a crossing inside the opening window (measured from this
    // whole physical touch-down, i.e. `touchstart` - NOT reset on a
    // fire-driven re-anchor) - see `EARLY_COMMIT_WINDOW_MS`'s own doc -
    // OR a near-45deg ambiguous read (`candidate.isAmbiguous`, see
    // `DIAGONAL_AMBIGUITY_RATIO`'s own doc) needs one more touchmove
    // sample agreeing with the SAME naive axis before it commits, using
    // the exact same one-slot `pendingDirection` confirmation mechanism
    // for both triggers. A later, confidently-dominant turn within the
    // SAME continuous drag is, by definition, already past the opening
    // window and not ambiguous (the finger is already moving decisively,
    // not freshly settling or wavering near a diagonal), so it fires
    // immediately exactly as before - this only ever adds a safety net
    // to a fresh touch-down's opening moments or a genuinely coin-flip
    // reading, never to an ordinary confident swipe, and never a fixed
    // time delay (only ever waits for the next real touchmove sample,
    // which for a continuous drag is immediate).
    const isEarly = event.timeStamp - this.gestureStartTime < EARLY_COMMIT_WINDOW_MS;
    if (isEarly || candidate.isAmbiguous) {
      if (candidate.direction !== this.pendingDirection) {
        this.pendingDirection = candidate.direction;
        return;
      }
    }
    this.pendingDirection = null;

    const direction = preferActionableDirection(candidate, dx, dy, this.getCurrentDirection());
    const result = this.onDirection(direction);

    // PR32 (touch fairness pass): re-anchor at the current point so
    // continued dragging can keep firing - UNLESS this direction was
    // rejected as an illegal reverse. A reverse reading this early is
    // very plausibly a brief geometric misread of a real intended turn
    // (a hook, or the natural curve of a fast swipe) rather than a
    // genuine "player tried to run into themselves" - holding the
    // anchor here (instead of resetting it to the current, barely-moved
    // point) lets the NEXT sample's displacement accumulate from where
    // the gesture actually started, giving the true intended direction
    // a fair chance to be read instead of losing it to a reset anchor.
    // Every other rejection reason (`"same-direction"`, `"queue-full"`)
    // re-anchors exactly as before - nothing was misread in those cases,
    // just intentionally not queued, so there's nothing to recover.
    if (result !== "illegal-reverse") {
      this.anchor = { x: touch.clientX, y: touch.clientY };
    }
  };

  /**
   * PR31: a fast, deliberate flick can legitimately produce zero (or zero
   * threshold-crossing) `touchmove` events before `touchend` fires - a
   * short enough gesture can complete, or the browser can coalesce/drop
   * intermediate move samples, entirely between two consecutive event
   * dispatches. Previously this handler only cleared `anchor`, so that
   * exact case - a real swipe whose distance only ever became evident at
   * its FINAL position - was silently dropped: not delayed, not queued,
   * just never recognized at all. `touchend` carries no `touches` (the
   * touch has already lifted) but does carry `changedTouches` - the
   * touch(es) that just ended - which this now evaluates with the exact
   * same `classifySwipeCandidate()` rule `touchmove` uses, so a swipe is
   * recognized identically whether its threshold was crossed mid-gesture
   * or only became evident at lift.
   *
   * This can never double-fire the same swipe: `onTouchMove` re-anchors at
   * its own fire point every time it fires (see that handler), so by the
   * time `touchend` runs, `anchor` already reflects the LAST recognized
   * position, not the gesture's original start - this evaluates only the
   * remaining, not-yet-recognized displacement since that last point,
   * exactly like one more `touchmove` sample would. No ambiguity
   * confirmation gate applies here (unlike `onTouchMove`) - this is
   * necessarily the FINAL sample of the gesture, so there's no next
   * sample left to confirm against; the current-direction-aware override
   * (`preferActionableDirection()`) still applies, since that's a
   * same-sample geometric judgment, not a multi-sample confirmation.
   *
   * Deliberately NOT shared with `touchcancel` (see `onTouchCancel`) - a
   * cancelled gesture (an interrupted/aborted touch, e.g. the OS taking
   * over for a system gesture) must never commit a direction change; only
   * a normal, completed lift does.
   */
  private readonly onTouchEnd = (event: TouchEvent): void => {
    event.preventDefault();
    if (this.activeTouchId === null) return;
    const touch = findTouchById(event.changedTouches, this.activeTouchId);
    if (!touch) return; // Some OTHER touch ended (e.g. an incidental palm contact lifting) - our own gesture, still in progress, must not be reset by it.
    if (this.anchor) {
      const dx = touch.clientX - this.anchor.x;
      const dy = touch.clientY - this.anchor.y;
      const candidate = classifySwipeCandidate(dx, dy, GameConfig.input.swipeMinDistance);
      if (candidate) {
        this.onDirection(preferActionableDirection(candidate, dx, dy, this.getCurrentDirection()));
      }
    }
    this.anchor = null;
    this.activeTouchId = null;
  };

  /** A cancelled gesture never commits a direction - only clears state. See `onTouchEnd`'s own doc for why this is deliberately a separate handler. Same "only the tracked touch" guard as `onTouchEnd` - some other touch cancelling must never reset a still-in-progress real gesture. */
  private readonly onTouchCancel = (event: TouchEvent): void => {
    event.preventDefault();
    if (this.activeTouchId === null) return;
    const touch = findTouchById(event.changedTouches, this.activeTouchId);
    if (!touch) return;
    this.anchor = null;
    this.activeTouchId = null;
  };

  // Belt-and-suspenders: block long-press context menu / text selection.
  // A stable bound reference (not an inline arrow function passed
  // straight to `addEventListener`) - `removeEventListener` can only
  // ever remove the exact function reference it was given, so an inline
  // closure here would be permanently unremovable by `stop()`, leaking
  // one listener per `start()` call (repeated start/stop, or a scene
  // Restart that tears down and rebuilds a `TouchInput` instance, would
  // accumulate listeners forever).
  private readonly onContextMenu = (event: Event): void => {
    event.preventDefault();
  };

  constructor(
    private readonly element: HTMLElement,
    private readonly onDirection: DirectionHandler,
    /**
     * PR32 (touch fairness pass): a live accessor for the snake's current
     * direction (e.g. `() => this.levelGame.getSnapshot().direction`) -
     * NOT a value captured once at construction time, since the real
     * current direction changes over the life of one `TouchInput`
     * instance. Read fresh on every candidate resolution
     * (`preferActionableDirection()`), never cached - see that
     * function's own doc for what it's used for.
     */
    private readonly getCurrentDirection: () => Direction,
  ) {}

  start(): void {
    this.element.addEventListener("touchstart", this.onTouchStart, { passive: false });
    this.element.addEventListener("touchmove", this.onTouchMove, { passive: false });
    this.element.addEventListener("touchend", this.onTouchEnd, { passive: false });
    this.element.addEventListener("touchcancel", this.onTouchCancel, { passive: false });
    this.element.addEventListener("contextmenu", this.onContextMenu);
  }

  stop(): void {
    this.element.removeEventListener("touchstart", this.onTouchStart);
    this.element.removeEventListener("touchmove", this.onTouchMove);
    this.element.removeEventListener("touchend", this.onTouchEnd);
    this.element.removeEventListener("touchcancel", this.onTouchCancel);
    this.element.removeEventListener("contextmenu", this.onContextMenu);
  }
}
